import Breadcrumb, { Crumb } from "../../../components/common/Breadcrumbs";
import React, { ChangeEvent, FC, FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Form from "../../../components/common/form/Form";
import { skipToken } from "@reduxjs/toolkit/query";
import { createOptionListForSelectTag } from "../../../helpers/utils/Utils";
import SelectField from "../../../components/common/form/SelectField";
import { handleRTKQuery } from "../../../helpers/utils/RtkQueryUtils";
import AuditableInfoButton from "../../../components/common/form/AuditableInfoButton";
import { Routes as AppRoutes } from "../../../routes";
import ButtonTray from "../../../components/common/form/ButtonTray";
import {
    useCreateBillReleaseMutation,
    useDeleteBillReleaseByIdMutation,
    useGetBillReleaseByIdQuery,
    useUpdateBillReleaseMutation
} from "./billReleaseApi";
import {useGetAllCountyByStateAbbrQuery, useLazyGetAllCountyByStateAbbrQuery} from "../counties/countiesApi";
import {useGetAllTenantsQuery} from "../../customer/tenantApi";
import {useGetAllStatesQuery} from "../states/statesApi";
import InputField from "../../../components/common/form/InputField";
import TextAreaField from "../../../components/common/form/TextAreaField";
import ModalButton from "../../../components/common/form/ModalButton";
import IconButton from "../../../components/common/form/IconButton";
import { FaTrash } from "react-icons/fa";
import ConfirmationModal from "../../../components/layout/modal/ConfirmationModal";
import { hasPermission } from "../../../helpers/utils/AccessControlUtils";

interface props {
    crumbs: Crumb[];
    title: string,
    btnText: string,
    isReadOnly?: boolean
}

const BillReleaseForm: FC<props> = ({ crumbs, title, btnText, isReadOnly }) => {

    const navigate = useNavigate();
    let { billReleaseId } = useParams();

    const [createBillRelease, createBillReleaseApiResponse] = useCreateBillReleaseMutation();
    const [updateBillRelease, updateBillReleaseApiResponse] = useUpdateBillReleaseMutation();
    const billReleaseByIdApi = useGetBillReleaseByIdQuery(billReleaseId ?? skipToken);
    const [billReleaseData, setBillReleaseData] = useState<any>({});
    const tenantApiData = useGetAllTenantsQuery("");
    const statesApiResult = useGetAllStatesQuery("");
    const countiesApiResult = useGetAllCountyByStateAbbrQuery(billReleaseData?.state?.value ?? skipToken);
    const [deleteCannedNotes, { isLoading }] = useDeleteBillReleaseByIdMutation();
    const [show, setShow] = useState(false);
    useEffect(() => {
        let obj = billReleaseByIdApi?.data;
        if (obj) {
            setBillReleaseData({
                id: obj.id,
                tenant: { label: obj.tenant, value: obj.tenant },
                counties: obj.county
                    ? { label: obj.county.countyName, value: obj.county.id }
                    : null,
                state: obj.county?.state
                    ? {
                        label: obj.county.state.stateName,
                        value: obj.county.state.stateAbbreviation,
                    }
                    : null,
                taxYear: obj.taxYear,
                released: obj.released,
                note: obj.note || "",
            });
        }
    }, [billReleaseByIdApi?.data]);


    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        let commonObj = {
            tenant: billReleaseData?.tenant?.label,
            county: {
                id: billReleaseData?.counties?.value
            },
            taxYear: billReleaseData?.taxYear,
            note: billReleaseData?.note,
            released: billReleaseData?.released,
        };

        const createOrUpdateBillRelease = async () => {
            if (btnText === "Create") {
                return await createBillRelease(commonObj).unwrap();
            } else {
                return await updateBillRelease({
                    id: billReleaseId,
                    requestBody: commonObj,
                }).unwrap();
            }
        };

        await handleRTKQuery(createOrUpdateBillRelease, (response: any) => {
            if (response?.success || response?.data?.success) {
                setBillReleaseData({});
                setShow(false);
                navigate(AppRoutes.BillRelease.path);
            }
        });
    }


    const handleOnChange = (e: ChangeEvent<HTMLInputElement> | {
        name: string;
        value: any
    } | ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = "target" in e ? e.target : e;
        setBillReleaseData({
            ...billReleaseData,
            [name]: value,
        })
    }
    const handleToggle = (property: string) => () => {
        setBillReleaseData((prevData: any) => ({
            ...prevData,
            [property]: !prevData[property]
        }));
    };

    const handleDelete = async () => {
        if (billReleaseId) {
            await deleteCannedNotes(billReleaseId).unwrap();
            setShow(false);
            navigate(AppRoutes.BillRelease.path);
        }
    };

    return (
        <>
            <div className="px-4 py-2">

                <div className={"justify-between sm:block md:flex lg:flex"}>
                    <Breadcrumb crumbs={crumbs} />
                    <div className={"justify-between sm:block md:flex lg:flex"}>
                        {billReleaseId && hasPermission(["BILLREL-D"]) && (
                            <>
                                <ModalButton
                                    triggerButton={
                                        <IconButton
                                            icon={<FaTrash />}
                                            title={"Delete Auto Hoa"}
                                            hasBackground={true}
                                            className="mr-2"
                                        />
                                    }
                                    modal={
                                        <ConfirmationModal
                                            show={show}
                                            setShow={setShow}
                                            title={"Delete Bill Release"}
                                            bodyMessage={"Are you sure you want to delete this Bill Release?"}
                                            onSubmit={handleDelete}
                                            isLoading={isLoading}
                                        />
                                    }
                                />
                            </>
                        )}
                        {hasPermission(["AUDIT-V"]) && <AuditableInfoButton obj={billReleaseByIdApi?.data} />
                        }

                    </div>
                </div>

                <div className="flex justify-center mt-3">
                    <div className="bg-text-0 lg:w-4/6 sm:w-full p-5 rounded-md shadow-[0_0_10px_-2px_rgba(0,0,0,0.2)]">
                        <h3 className="text-xl font-semibold text-surface-0 pb-3 border-b border-surface-7">{title}</h3>
                        <Form onSubmit={handleSubmit} fields={<>
                            <div className="grid grid-cols-12">


                                <SelectField
                                    placeholder='Select Tenant...'
                                    value={billReleaseData?.tenant}
                                    options={createOptionListForSelectTag(tenantApiData?.data, "name", "id")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({ name: "tenant", value: selectedOption })
                                    }}
                                    label={"Tenant"}
                                    required={true}
                                    isDisabled={isReadOnly}
                                />
                                <SelectField
                                    placeholder='Select State...'
                                    value={billReleaseData?.state}
                                    options={createOptionListForSelectTag(statesApiResult?.data, "stateName", "stateAbbreviation")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({ name: "state", value: selectedOption })
                                    }}
                                    label={"State"}
                                    required={true}
                                    isDisabled={isReadOnly}
                                />

                                <SelectField
                                    placeholder='Select County...'
                                    value={billReleaseData?.counties}
                                    options={createOptionListForSelectTag(countiesApiResult?.data, "countyName", "id")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({ name: "counties", value: selectedOption })
                                    }}
                                    label={"County"}
                                    isDisabled={isReadOnly}

                                />

                                <InputField
                                    name={"taxYear"}
                                    label="Tax Year"
                                    onChange={handleOnChange}
                                    value={billReleaseData?.taxYear || ""}
                                    type="number"
                                    placeholder="Enter Tax Year..."
                                    required={true}
                                    disabled={isReadOnly}
                                />

                                <TextAreaField
                                    label={"Note"}
                                    placeholder={"Enter Note...."}
                                    onChange={handleOnChange}
                                    disabled={isReadOnly}
                                    name={"note"}
                                    value={billReleaseData?.note || ""}
                                />

                                <InputField
                                    name="released"
                                    label="Released"
                                    onChange={handleToggle("released")}
                                    checked={billReleaseData.released || ""}
                                    type="checkbox"
                                    disabled={isReadOnly}
                                />
                            </div>

                            {!isReadOnly &&
                                <div className="col-span-12 md:grid md:grid-cols-12 gap-3">
                                    <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                    </div>
                                    <div className="relative col-span-9">
                                        <div className={"mt-4 flex"}>
                                            <ButtonTray
                                                buttons={[
                                                    {
                                                        buttonProps: {
                                                            btnText: "Cancel",
                                                            onClick: () => navigate(AppRoutes.BillRelease.path),
                                                            type: "cancel",
                                                        },
                                                        buttonType: "button"
                                                    },
                                                    {
                                                        buttonProps: {
                                                            btnText: "Submit",
                                                            type: "submit",
                                                            isLoading: (createBillReleaseApiResponse.isLoading || updateBillReleaseApiResponse.isLoading),

                                                        },
                                                        buttonType: "button",

                                                    }
                                                ]}
                                                align="end"
                                                gap={2}
                                            />
                                        </div>
                                    </div>
                                </div>
                            }


                        </>}
                        />

                    </div>
                </div>
            </div>
        </>
    )
}
export default BillReleaseForm;